import { useEffect, useRef, useState } from 'react';

type Status = 'idle' | 'submitting' | 'success' | 'error';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function EmailModal({ open, onClose }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMessage(null);
    try {
      const res = await fetch('/api/early-access', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, source: 'landing' }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? 'request_failed');
      }
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error && err.message === 'invalid_request'
          ? 'That email looks invalid. Try another?'
          : 'Something went wrong. Try again in a moment.'
      );
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ea-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-bg-base/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="surface-elevated relative w-full max-w-md p-7 animate-fade-up">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1.5 text-ink-muted hover:bg-bg-elevated hover:text-ink-primary"
          aria-label="Close"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {status === 'success' ? (
          <div className="text-center">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="m5 12 5 5L20 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 id="ea-title" className="mt-4 text-[18px] font-semibold">
              You&apos;re on the list.
            </h2>
            <p className="mt-2 text-[14px] text-ink-muted">
              We&apos;ll be in touch as design-partner slots open.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost mt-5 px-4 py-2"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 id="ea-title" className="text-[18px] font-semibold">
              Request early access
            </h2>
            <p className="mt-1.5 text-[14px] text-ink-muted">
              We&apos;ll reach out as design-partner slots open. No marketing
              email — just a real reply.
            </p>
            <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-ink-secondary">
                  Work email
                </span>
                <input
                  ref={inputRef}
                  type="email"
                  required
                  autoComplete="email"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-md border border-border bg-bg-elevated px-3 py-2.5 text-[14px] text-ink-primary placeholder:text-ink-faint focus:border-accent focus:outline-none"
                />
              </label>
              {errorMessage && (
                <p className="text-[12px] text-amber-300">{errorMessage}</p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-primary mt-1 px-4 py-2.5 disabled:opacity-60"
              >
                {status === 'submitting' ? 'Sending…' : 'Request access'}
              </button>
              <p className="text-[11px] text-ink-faint">
                By submitting you agree to the{' '}
                <a href="/privacy.html" className="underline hover:text-ink-secondary">
                  privacy policy
                </a>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
